import { fetch, fetchData } from '@/services/fetch'
import { getParams } from '@/services/params'

export type InvoiceListResponse = {
  '@context': string
  '@id': string
  '@type': 'hydra:Collection'
  'hydra:member': InvoiceListItem[]
  'hydra:totalItems': number
  'hydra:view': any
}

const buildUrl = (base: string, params?: any[]): string => {
  if (!params) return base
  const searchParams = getParams(params)
  return searchParams.size ? `${base}?${searchParams.toString()}` : base
}

// `basePath` lets the "Invoice type" filter target a subtype collection
// (e.g. /credit_invoices) instead of /invoices. All base-entity ApiFilters
// (orderBy, search, date, groups[]) are inherited by the subtype collections,
// so the query params are identical — only the discriminator scope changes.
const getInvoices = async (
  params?: any[],
  basePath: string = '/invoices'
): Promise<InvoiceListResponse> => {
  return await fetch(buildUrl(basePath, params))
}

const getInvoice = async (id: string, params?: any[]): Promise<InvoiceEntity> => {
  return await fetch(buildUrl(`/invoices/${id}`, params))
}

const getInvoiceChildInvoices = async (
  id: string,
  params?: any[]
): Promise<{ items: InvoiceEntity[]; total: number }> => {
  const response = (await fetch(buildUrl(`/invoices/${id}/child_invoices`, params))) as {
    'hydra:member'?: InvoiceEntity[]
    'hydra:totalItems'?: number
  }
  return {
    items: response['hydra:member'] ?? [],
    total: response['hydra:totalItems'] ?? 0
  }
}

const getInvoiceTransitions = async (id: string): Promise<InvoiceTransitionEntry[]> => {
  // Dedicated GET /invoices/{id}/transitions (InvoiceWorkflowLog, groups
  // workflow:read+blameable+timestampable). Add user:list (whitelisted on that
  // resource, overrideDefaultGroups:false) so createdBy serialises with the
  // acting user's email instead of a bare IRI — otherwise the "By" column is
  // empty. user:list is the lean projection (user:read is too heavy here).
  const response = await fetch(
    buildUrl(`/invoices/${id}/transitions`, [{ 'groups[]': 'user:list' }])
  )
  return (response as { 'hydra:member'?: InvoiceTransitionEntry[] })['hydra:member'] ?? []
}

const updateInvoice = async (id: string, data: Record<string, unknown>): Promise<InvoiceEntity> => {
  // `standard_put:false` is set globally, so the generic PUT merges ONLY the
  // fields sent — a partial body (e.g. {billingAddress} or {invoicingNote})
  // changes just those without re-validating the whole entity graph. pm:read
  // fields like invoicingNote are writable thanks to AdminContextBuilder's
  // request-time pm:write injection for ROLE_ADMIN/ROLE_PM.
  return await fetch(`/invoices/${id}`, { method: 'put', data })
}

const completePayment = async (paymentId: string): Promise<void> => {
  // PUT /payments/{id}/complete — Payment workflow transition
  // (created / processing / amount_capturable_updated / failed -> succeeded).
  // deserialize:false on the API, so the body is ignored.
  await fetch(`/payments/${paymentId}/complete`, { method: 'put', data: {} })
}

const createAdjustmentItem = async (payload: Record<string, unknown>): Promise<InvoiceItemLite> => {
  // POST /adjustment_items (item:write + calculable:write). Body carries
  // partOfInvoice (/invoices/{id} IRI), name/description/quantity/unitPrice/
  // price/priceUnit. The server recomputes totalPrice (incl. VAT).
  return await fetch('/adjustment_items', { method: 'post', data: payload })
}

const updateAdjustmentItem = async (
  itemId: string,
  payload: Record<string, unknown>
): Promise<InvoiceItemLite> => {
  // PUT /adjustment_items/{id} — same AdjustmentItemStateProcessor as create, so
  // it recomputes the draft's totals + payment. standard_put:false → partial body.
  return await fetch(`/adjustment_items/${itemId}`, { method: 'put', data: payload })
}

const deleteAdjustmentItem = async (itemId: string): Promise<void> => {
  await fetch(`/adjustment_items/${itemId}`, { method: 'delete' })
}

const deleteInvoice = async (id: string): Promise<void> => {
  // PUT /invoices/{id}/delete — workflow `delete` transition
  // (ProcessInvoiceTransitionStateProcessor): draft -> deleted, sets deletedAt +
  // deletedBy. deserialize:false, so the body is ignored. The raw DELETE only
  // soft-deletes and leaves status=draft (no deletedBy), so the draft looked
  // undeleted — this is the proper old-backoffice delete path.
  await fetch(`/invoices/${id}/delete`, { method: 'put', data: {} })
}

const finalizeInvoice = async (typePath: string, id: string): Promise<InvoiceEntity> => {
  // PUT /{type}_invoices/{id}/finalize (FinalizeInvoiceStateProcessor,
  // deserialize:false — body ignored). draft -> open.
  return await fetch(`/${typePath}/${id}/finalize`, { method: 'put', data: {} })
}

const cancelInvoice = async (id: string, payload: InvoiceCancelPayload): Promise<InvoiceEntity> => {
  // Send only the minimal cancelable:write fields — re-sending the whole invoice
  // would re-validate the full entity graph (422 on unrelated legacy data) and
  // risk the nested-relation PUT-500. The cancellation message is what fills the
  // notification email body (without it the email is empty — bug B2).
  return await fetch(`/invoices/${id}/cancel`, {
    method: 'put',
    data: payload
  })
}

const syncInvoicePaymentStatus = async (id: string): Promise<InvoiceEntity> => {
  // `deserialize: false` on the API side — body is ignored; the processor queries
  // Buchhaltungsbutler. With the #1615 fix, an unpaid invoice no longer 500s.
  return await fetch(`/invoices/${id}/update_accounting_payment_status`, {
    method: 'post',
    data: {}
  })
}

const sendInvoiceToCustomer = async (id: string): Promise<InvoiceEntity> => {
  // POST /invoices/{id}/sendtocustomer sends regardless of the autosend flag —
  // the recovery path for invoices whose autosend was turned off at finalize.
  return await fetch(`/invoices/${id}/sendtocustomer`, {
    method: 'post',
    data: {}
  })
}

const switchInvoiceCollectionMandate = async (id: string): Promise<InvoiceEntity> => {
  // #2033 — debits the invoice from the SEPA mandate of the company's current contact person and
  // emails the customer the new debit date. No body; the API picks the mandate.
  return await fetch(`/invoices/${id}/switch_collection_mandate`, {
    method: 'post',
    data: {}
  })
}

const setInvoiceAutosend = async (id: string, autosend: boolean): Promise<InvoiceEntity> => {
  // `autosend` is in the pm:write group (injected for ROLE_ADMIN/ROLE_PM by
  // AdminContextBuilder). standard_put:false means only this field changes.
  return await fetch(`/invoices/${id}`, {
    method: 'put',
    data: { autosend }
  })
}

const getInvoiceAuditLog = async (id: string): Promise<InvoiceAuditLogEntry[]> => {
  // /price-history mirrors the CCN audit-log endpoint — Gedmo's LogEntryRepository
  // serialises directly, so the response is a plain array.
  const response = await fetch(`/invoices/${id}/price-history`)
  return Array.isArray(response) ? response : []
}

// Server parses the {from}/{to} path segments with #[MapDateTime(format: 'Y.m.d')]
// (dot-separated), so the wire format must be exactly YYYY.MM.DD.
const toDatevDate = (date: Date): string =>
  `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
    date.getDate()
  ).padStart(2, '0')}`

// GET /invoices/export_datev/{from}/{to}/{cancelled}. cancelled=0 ("ignore")
// matches the old backoffice "All invoices" export — a date-range-only export
// (the range applies to deliveryDate/cancellationDate server-side). Returns the
// generated XLSX as a Blob.
const exportInvoicesDatev = async (fromDate: Date, toDate: Date): Promise<Blob> => {
  return await fetch(`/invoices/export_datev/${toDatevDate(fromDate)}/${toDatevDate(toDate)}/0`, {
    method: 'GET',
    responseType: 'blob'
  })
}

// POST /{path}/create (e.g. /credit_invoices/create). validate:false on the API;
// the resource class fixes the subtype, so only `customer` is required — the
// processor's setupInvoice() populates dates/amounts/accounting defaults.
const createInvoice = async (
  typePath: string,
  customerIri: string,
  cartIri?: string
): Promise<InvoiceEntity> => {
  const data: { customer: string; cart?: string } = { customer: customerIri }
  // Adjustment invoices are tied to a cart (writable on the create endpoint).
  if (cartIri) data.cart = cartIri
  return await fetch(`/${typePath}/create`, {
    method: 'post',
    data
  })
}

// Customer lookup for the create wizard — mirrors the old backoffice's
// multi-field customer search (any of email/first/last/company matches).
const searchInvoiceCustomers = async (query: string): Promise<CustomerEntity[]> => {
  const term = query.trim()
  if (!term) return []
  // OR-search across email / first / last / company name. The company filter key is
  // `company.name` (the OrSearchFilter property) — `companyName` is not a real param
  // and was silently ignored, so company-name search didn't work.
  const response = await fetchData<CustomerEntity>(
    '/customers',
    { email: term, firstName: term, lastName: term, 'company.name': term },
    null,
    ['user:list', 'company:read']
  )
  return response['hydra:member'] ?? []
}

// --- Collective Invoice Editor (#1966) — compose-from-cancelled ---

// Unbilled child d-invoices eligible to add to a composed collective:
// part-of-collective, no parent, draft. Filtered to the source's company
// client-side (the picker set is small). Mirrors the CCN editor's eligible-jobs.
const getEligibleChildInvoices = async (companyId: string): Promise<InvoiceEntity[]> => {
  const response = await fetch(
    buildUrl('/invoices', [
      { partOfCollectiveInvoice: true },
      { 'exists[parentInvoice]': false },
      { status: 'draft' },
      // Scope to the collective's company SERVER-SIDE — otherwise a global page
      // of drafts is fetched and a busy DB pushes this company's invoices past
      // the page window, hiding released d-invoices from the picker.
      { 'customer.company.id': companyId },
      { itemsPerPage: 100 },
      { 'groups[]': 'invoice:list' },
      { 'groups[]': 'calculable:list' },
      { 'groups[]': 'item:read' },
      // order:list carries the order's orderName + status (lean, no N+1) so the
      // editor knows which orders are DELIVERED (editable).
      { 'groups[]': 'order:list' },
      { 'groups[]': 'customer:read' },
      { 'groups[]': 'company:read' }
    ])
  )
  return (response as { 'hydra:member'?: InvoiceEntity[] })['hydra:member'] ?? []
}

// POST /invoices/{id}/draft_pdf — watermark-free PDF preview of the composed
// collective. Zero DB writes (mirrors the CCN draft preview).
const previewCollectiveDraft = async (id: string, payload: InvoiceDraftPayload): Promise<Blob> => {
  return await fetch(`/invoices/${id}/draft_pdf`, {
    method: 'post',
    data: payload,
    responseType: 'blob'
  })
}

// POST /invoices/{id}/create_from_cancelled — atomically compose the corrected
// replacement collective; returns the new invoice's id/name/status.
const createCollectiveFromCancelled = async (
  id: string,
  payload: InvoiceDraftPayload
): Promise<{ id: string; name: string | null; status: string; sent: boolean }> => {
  return await fetch(`/invoices/${id}/create_from_cancelled`, {
    method: 'post',
    data: payload
  })
}

export {
  getEligibleChildInvoices,
  previewCollectiveDraft,
  createCollectiveFromCancelled,
  getInvoices,
  getInvoice,
  getInvoiceChildInvoices,
  cancelInvoice,
  syncInvoicePaymentStatus,
  sendInvoiceToCustomer,
  switchInvoiceCollectionMandate,
  setInvoiceAutosend,
  getInvoiceAuditLog,
  getInvoiceTransitions,
  updateInvoice,
  completePayment,
  createAdjustmentItem,
  updateAdjustmentItem,
  deleteAdjustmentItem,
  deleteInvoice,
  finalizeInvoice,
  exportInvoicesDatev,
  createInvoice,
  searchInvoiceCustomers
}
